import { createContext, useContext, useEffect, useRef, useState, type PropsWithChildren } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { setAccessToken } from "./api";
import { decodeCredential, parseWorkspaceDomains, validateWorkspaceIdentity, type GoogleIdentityClaims } from "./googleIdentity";

type GoogleCredentialResponse = { credential: string };
type GoogleIdentity = {
  accounts: { id: {
    initialize(config: { client_id: string; callback: (response: GoogleCredentialResponse) => void; hd?: string }): void;
    renderButton(element: HTMLElement, options: Record<string, unknown>): void;
    disableAutoSelect(): void;
  } };
};

declare global { interface Window { google?: GoogleIdentity } }

const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const workspaceDomains = parseWorkspaceDomains(import.meta.env.VITE_GOOGLE_WORKSPACE_DOMAINS as string | undefined);

const SignedInIdentity = createContext<GoogleIdentityClaims | null>(null);
export function useSignedInIdentity() { return useContext(SignedInIdentity); }

export function AuthGate({ children }: PropsWithChildren) {
  const queries = useQueryClient();
  const button = useRef<HTMLDivElement>(null);
  const [identity, setIdentity] = useState<GoogleIdentityClaims | null>(() => {
    try {
      const stored = sessionStorage.getItem("reco.google.token");
      return stored ? validateWorkspaceIdentity(decodeCredential(stored), workspaceDomains) : null;
    } catch { return null; }
  });
  const [token, setToken] = useState(() => {
    let storedToken: string | null = null;
    try { if (identity) storedToken = sessionStorage.getItem("reco.google.token"); } catch { /* Storage can be disabled. */ }
    // Restore the bearer token before authenticated children can start queries.
    // Waiting for an effect creates a refresh-only race where the first request is unauthenticated.
    setAccessToken(storedToken ?? undefined);
    return storedToken;
  });
  const [ready, setReady] = useState(!clientId);
  const [authError, setAuthError] = useState<string>();
  const blocked = !identity || !token;
  const owner = useRef(identity ? identity.sub ?? identity.email.toLowerCase() : undefined);
  const hasOpened = useRef(Boolean(identity && token));

  useEffect(() => {
    if (!clientId) return;
    const expire = () => {
      setAccessToken(undefined);
      setToken(null);
      try { sessionStorage.removeItem("reco.google.token"); } catch { /* Storage can be disabled. */ }
    };
    window.addEventListener("reco:session-expired", expire);
    const timer = identity && token ? window.setTimeout(expire, Math.max(0, identity.exp * 1000 - Date.now() - 10_000)) : undefined;
    return () => { window.removeEventListener("reco:session-expired", expire); window.clearTimeout(timer); };
  }, [identity, token]);

  useEffect(() => {
    if (!clientId || !blocked) return;
    const initialize = () => {
      if (!window.google || !button.current) return false;
      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          hd: "*",
          callback: ({ credential }) => {
            try {
              const user = validateWorkspaceIdentity(decodeCredential(credential), workspaceDomains);
              const nextOwner = user.sub ?? user.email.toLowerCase();
              if (owner.current && owner.current !== nextOwner) throw new Error("Your open work belongs to another account. Continue with the same account, or sign out and reload before switching accounts.");
              owner.current = nextOwner;
              hasOpened.current = true;
              try {
                sessionStorage.setItem("reco.google.token", credential);
                sessionStorage.setItem("reco.google.identity", JSON.stringify(user));
              } catch { /* Continue for this tab when storage is disabled. */ }
              setAccessToken(credential);
              setToken(credential);
              setIdentity(user);
              setAuthError(undefined);
              void queries.invalidateQueries();
            } catch (error) {
              setAuthError(error instanceof Error ? error.message : "Google sign-in was not accepted");
            }
          },
        });
        button.current.replaceChildren();
        window.google.accounts.id.renderButton(button.current, { theme: "outline", size: "large", shape: "pill", text: "continue_with", width: 290 });
        if (button.current.childElementCount === 0) throw new Error("Google did not render the sign-in control");
        setReady(true);
        setAuthError(undefined);
      } catch {
        setReady(true);
        setAuthError("Google sign-in could not initialize. Refresh the page or contact the ERP administrator.");
      }
      return true;
    };
    if (initialize()) return;
    const timer = window.setInterval(() => {
      if (initialize()) {
        window.clearInterval(timer);
        window.clearTimeout(timeout);
      }
    }, 100);
    const timeout = window.setTimeout(() => {
      window.clearInterval(timer);
      setReady(true);
      setAuthError("Google sign-in could not load. Check your connection, then refresh the page.");
    }, 10_000);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(timeout);
    };
  }, [blocked]);

  if (!clientId) return children;
  return <SignedInIdentity.Provider value={identity}>
    {hasOpened.current ? <div inert={blocked || undefined} aria-hidden={blocked || undefined}>{children}</div> : null}
    {blocked ? (
      <main className="login-shell" style={hasOpened.current ? { position: "fixed", inset: 0, zIndex: 10000 } : undefined} role="dialog" aria-modal="true" aria-label="Google sign-in">
        <section className="login-card">
          <div className="login-brand"><span>R</span></div>
          <p className="login-kicker">RECO ONE</p>
          <h1>Equipment operations,<br />one system.</h1>
          <p>Sign in with an authorized RECO Google Workspace account.</p>
          {hasOpened.current ? <p role="status">Your session expired. Your open forms are preserved. Sign in again with the same account to continue.</p> : null}
          <div className="google-signin" ref={button} />
          {!ready ? <div className="signin-status" role="status">Loading secure sign-in…</div> : null}
          {authError ? <p className="error-state compact">{authError}</p> : null}
          <small>Authorized workforce access only</small>
        </section>
      </main>
    ) : null}
  </SignedInIdentity.Provider>;
}

export function signOut() {
  sessionStorage.removeItem("reco.google.token");
  sessionStorage.removeItem("reco.google.identity");
  setAccessToken(undefined);
  window.google?.accounts.id.disableAutoSelect();
  window.location.reload();
}
